export const PLAN_DIMENSIONS = {
  GOAL_ALIGNMENT: '目标相关性',
  FEASIBILITY: '条件可执行性',
  ACTION_FIT: '动作适配性',
  LOAD_RECOVERY: '训练量与恢复',
  PROGRESSION: '阶段安排',
};
export const PLAN_FINDING_STATES = {
  SUPPORTED: '有支持证据',
  CONCERN: '存在疑点',
  CONFLICT: '明确冲突',
  INSUFFICIENT_EVIDENCE: '证据不足',
};
export const PLAN_TASK_STATES = {
  accepted: '已提交，等待后台处理',
  pending: '等待处理',
  running: '正在分析',
  failed: '分析失败',
  succeeded: '分析已生成',
  not_found: '尚无分析任务',
  disabled: '计划分析尚未启用',
};
// 与 Detail CurrentPlanAssessmentFramework 对齐的协议版本常量。
// schemaVersion 与 promptVersion 是两条独立演化的版本轴，不能恢复相等捷径校验；
// 提示词升版时必须与 Workwx mapper 和 trainingAdvice.js 三端同步核对（见方案05回归清单第11条）。
export const WHOLE_PLAN_SCHEMA_VERSION = 'whole-plan-assessment@1';
export const WHOLE_PLAN_PROMPT_VERSION = 'whole-plan-assessment@3';
// 单课框架的 promptVersion 与 schemaVersion 当前同值；Detail 单独上调时需同步此常量。
export const SINGLE_COURSE_FRAMEWORK_VERSION = 'current-plan-assessment-framework@1';
export function readPlanAnalysis(response, query, submitting = false) {
  const whole = Number(query.courseType) === 0;
  if (whole && Number(query.courseId) !== Number(query.planId))
    throw new Error('Invalid whole plan identity');
  const data = response?.data;
  const states = submitting
    ? ['accepted', 'disabled']
    : Object.keys(PLAN_TASK_STATES).filter((s) => s !== 'accepted');
  if (
    !['0', '00000'].includes(String(response?.code)) ||
    data?.schemaVersion !== 'plan-analysis-task-view@1' ||
    !states.includes(data?.status) ||
    ['memberId', 'planId', 'courseId', 'courseType'].some(
      (key) => Number(data?.[key]) !== Number(query[key]),
    )
  )
    throw new Error('Invalid plan analysis response');
  if (['disabled', 'not_found'].includes(data.status))
    return { status: data.status };
  if (
    typeof data.taskId !== 'string' ||
    !/^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(
      data.taskId,
    ) ||
    (query.taskId && query.taskId !== data.taskId)
  )
    throw new Error('Invalid task identity');
  if (submitting) return { status: data.status, taskId: data.taskId };
  if (
    !Number.isInteger(data.attemptCount) ||
    data.attemptCount < 0 ||
    data.attemptCount > 3
  )
    throw new Error('Invalid attempts');
  const result = { ...data, resultJson: undefined, assessment: null };
  if (data.status !== 'succeeded') return result;
  if (typeof data.resultJson !== 'string' || data.resultJson.length > 32000)
    throw new Error('Invalid result');
  const assessment = JSON.parse(data.resultJson);
  const expectedSchemaVersion = whole
    ? WHOLE_PLAN_SCHEMA_VERSION
    : SINGLE_COURSE_FRAMEWORK_VERSION;
  const expectedPromptVersion = whole
    ? WHOLE_PLAN_PROMPT_VERSION
    : SINGLE_COURSE_FRAMEWORK_VERSION;
  if (
    assessment.schemaVersion !== expectedSchemaVersion ||
    assessment.promptVersion !== expectedPromptVersion ||
    assessment.processingStatus !== 'analyzed' ||
    assessment.scope !==
      (whole ? 'WHOLE_CURRENT_PLAN' : 'SINGLE_PLANNED_COURSE') ||
    ['memberId', 'planId', 'courseId', 'courseType'].some(
      (key) => assessment[key] !== Number(query[key]),
    ) ||
    !Number.isSafeInteger(assessment.capturedAt) ||
    assessment.capturedAt <= 0 ||
    assessment.coverage?.wholePlanAssessed !== whole ||
    (!whole && assessment.coverage?.includedCourseCount !== 1) ||
    !Array.isArray(assessment.dimensions) ||
    assessment.dimensions.length !== 5
  )
    throw new Error('Invalid assessment scope');
  const seen = new Set();
  const text = (v, max) =>
    typeof v === 'string' && !!v.trim() && v.length <= max;
  const list = (v, min, max, len) =>
    Array.isArray(v) &&
    v.length >= min &&
    v.length <= max &&
    v.every((s) => text(s, len)) &&
    new Set(v).size === v.length;
  if (
    whole &&
    (!Number.isInteger(assessment.coverage.includedCourseCount) ||
      assessment.coverage.includedCourseCount < 1 ||
      assessment.coverage.includedCourseCount > 100 ||
      assessment.coverage.totalPlanCourseCount !==
        assessment.coverage.includedCourseCount)
  )
    throw new Error('Incomplete whole plan coverage');
  assessment.dimensions.forEach((d) => {
    if (
      !Object.prototype.hasOwnProperty.call(PLAN_DIMENSIONS, d.dimension) ||
      seen.has(d.dimension) ||
      !Object.prototype.hasOwnProperty.call(PLAN_FINDING_STATES, d.status) ||
      !text(d.rationale, 400) ||
      !text(d.nextCheck, 200) ||
      !list(d.evidenceRefs, 1, whole ? 6 : 12, 128) ||
      (!whole &&
        d.dimension === 'PROGRESSION' &&
        d.status !== 'INSUFFICIENT_EVIDENCE') ||
      (['ACTION_FIT', 'LOAD_RECOVERY'].includes(d.dimension) &&
        d.status === 'SUPPORTED') ||
      !list(
        d.missingEvidence,
        d.status === 'INSUFFICIENT_EVIDENCE' ? 1 : 0,
        5,
        160,
      )
    )
      throw new Error('Invalid dimension');
    seen.add(d.dimension);
  });
  if (!list(assessment.coverage.sourceMissingEvidence, 0, whole ? 40 : 20, 160))
    throw new Error('Invalid coverage');
  if (whole) {
    const used = new Set(assessment.dimensions.flatMap((d) => d.evidenceRefs));
    const provided = new Set();
    if (
      !Array.isArray(assessment.evidenceSources) ||
      assessment.evidenceSources.length > 30
    )
      throw new Error('Missing evidence excerpts');
    assessment.evidenceSources.forEach((s) => {
      if (
        !s ||
        Object.keys(s).length !== 2 ||
        !used.has(s.ref) ||
        provided.has(s.ref) ||
        !/^(plan|schedule|(?:member\.fact|execution|action|course|stage|scheduled)\.[1-9][0-9]{0,9})$/.test(
          s.ref,
        ) ||
        !text(s.excerpt, 160)
      )
        throw new Error('Invalid evidence excerpt');
      provided.add(s.ref);
    });
    if (used.size !== provided.size)
      throw new Error('Missing referenced evidence');
  }
  result.assessment = assessment;
  return result;
}
